import { useMemo } from 'react';

const MM_PER_INCH = 25.4;
const DEFAULT_DPI = 96;

function toPx(mm) {
  return (mm / MM_PER_INCH) * DEFAULT_DPI;
}

function Svg({ width, height, children }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      {children}
    </svg>
  );
}

function Background({ width, height }) {
  return <rect width={width} height={height} fill="#ffffff" />;
}

function SquareGridPattern({ spacing, minorWidth, majorWidth, color, width, height, accentEvery }) {
  return useMemo(() => {
    const sp = toPx(spacing);
    const elements = [];

    for (let x = 0; x <= width; x += sp) {
      if (accentEvery > 0 && Math.round(x / sp) % accentEvery === 0) {
        elements.push(<line key={`mv${x}`} x1={x} y1={0} x2={x} y2={height} stroke={color} strokeWidth={majorWidth} opacity={1} />);
      } else {
        elements.push(<line key={`v${x}`} x1={x} y1={0} x2={x} y2={height} stroke={color} strokeWidth={minorWidth} opacity={0.45} />);
      }
    }
    for (let y = 0; y <= height; y += sp) {
      if (accentEvery > 0 && Math.round(y / sp) % accentEvery === 0) {
        elements.push(<line key={`mh${y}`} x1={0} y1={y} x2={width} y2={y} stroke={color} strokeWidth={majorWidth} opacity={1} />);
      } else {
        elements.push(<line key={`h${y}`} x1={0} y1={y} x2={width} y2={y} stroke={color} strokeWidth={minorWidth} opacity={0.45} />);
      }
    }
    return (
      <Svg width={width} height={height}>
        <Background width={width} height={height} />
        {elements}
      </Svg>
    );
  }, [spacing, minorWidth, majorWidth, color, width, height, accentEvery]);
}

function DotGrid({ spacing, minorWidth, color, width, height }) {
  return useMemo(() => {
    const sp = toPx(spacing);
    const r = Math.max(minorWidth * 1.5, 1);
    const dots = [];
    const cols = Math.ceil(width / sp) + 1;
    const rows = Math.ceil(height / sp) + 1;
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        dots.push(<circle key={`${row}-${col}`} cx={col * sp} cy={row * sp} r={r} fill={color} opacity={0.5} />);
      }
    }
    return (
      <Svg width={width} height={height}>
        <Background width={width} height={height} />
        {dots}
      </Svg>
    );
  }, [spacing, minorWidth, color, width, height]);
}

function IsometricGrid({ spacing, minorWidth, color, width, height }) {
  return useMemo(() => {
    const lines = [];
    const sp = toPx(spacing);
    const lw = minorWidth;
    const angle = Math.PI / 6;
    const diag = Math.ceil(Math.sqrt(width * width + height * height));
    const step = sp * Math.cos(angle);
    const count = Math.ceil(diag / step);

    for (let i = -count; i <= count; i++) {
      const x1 = i * step;
      const y1 = i * sp * Math.sin(angle);
      const x2 = x1 + diag * Math.cos(-angle);
      const y2 = y1 + diag * Math.sin(-angle);
      lines.push(
        <line key={`r${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={lw} opacity={0.45} />
      );
    }

    for (let i = -count; i <= count; i++) {
      const x1 = i * step;
      const y1 = -i * sp * Math.sin(angle);
      const x2 = x1 + diag * Math.cos(angle);
      const y2 = y1 + diag * Math.sin(angle);
      lines.push(
        <line key={`l${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={lw} opacity={0.45} />
      );
    }

    return (
      <Svg width={width} height={height}>
        <Background width={width} height={height} />
        {lines}
      </Svg>
    );
  }, [spacing, minorWidth, color, width, height]);
}

function HexagonalGrid({ spacing, minorWidth, color, width, height }) {
  return useMemo(() => {
    const elements = [];
    const sp = toPx(spacing);
    const lw = minorWidth;
    const hexW = sp;
    const hexH = sp * Math.sqrt(3);
    const dx = hexW * 1.5;
    const dy = hexH;
    const cols = Math.ceil(width / dx) + 2;
    const rows = Math.ceil(height / dy) + 2;

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const cx = col * dx + (row % 2 === 0 ? 0 : dx / 2);
        const cy = row * dy + dy / 2;
        const pts = [];
        for (let i = 0; i < 6; i++) {
          const a = (Math.PI / 3) * i - Math.PI / 6;
          pts.push(`${(cx + hexW * 0.5 * Math.cos(a)).toFixed(2)},${(cy + hexW * 0.5 * Math.sin(a)).toFixed(2)}`);
        }
        elements.push(
          <polygon key={`hex-${row}-${col}`} points={pts.join(' ')} fill="none" stroke={color} strokeWidth={lw} opacity={0.45} />
        );
      }
    }
    return (
      <Svg width={width} height={height}>
        <Background width={width} height={height} />
        {elements}
      </Svg>
    );
  }, [spacing, minorWidth, color, width, height]);
}

function PolarGrid({ spacing, minorWidth, color, width, height }) {
  return useMemo(() => {
    const elements = [];
    const sp = toPx(spacing);
    const lw = minorWidth;
    const cx = width / 2;
    const cy = height / 2;
    const maxR = Math.sqrt(cx * cx + cy * cy);

    for (let r = sp; r <= maxR; r += sp) {
      elements.push(
        <circle key={`c${r}`} cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={lw} opacity={0.45} />
      );
    }

    for (let a = 0; a < 360; a += 30) {
      const rad = (a * Math.PI) / 180;
      elements.push(
        <line key={`r${a}`} x1={cx} y1={cy} x2={cx + maxR * Math.cos(rad)} y2={cy + maxR * Math.sin(rad)} stroke={color} strokeWidth={lw} opacity={0.45} />
      );
    }

    return (
      <Svg width={width} height={height}>
        <Background width={width} height={height} />
        {elements}
      </Svg>
    );
  }, [spacing, minorWidth, color, width, height]);
}

function EngineeringGrid({ spacing, minorWidth, majorWidth, color, width, height }) {
  return useMemo(() => {
    const lines = [];
    const sp = toPx(spacing);
    const majorStep = 5;

    for (let x = 0; x <= width; x += sp) {
      const idx = Math.round(x / sp);
      const isMajor = idx % majorStep === 0;
      lines.push(
        <line key={`v${x}`} x1={x} y1={0} x2={x} y2={height} stroke={color} strokeWidth={isMajor ? majorWidth : minorWidth} opacity={isMajor ? 1 : 0.4} />
      );
    }
    for (let y = 0; y <= height; y += sp) {
      const idx = Math.round(y / sp);
      const isMajor = idx % majorStep === 0;
      lines.push(
        <line key={`h${y}`} x1={0} y1={y} x2={width} y2={y} stroke={color} strokeWidth={isMajor ? majorWidth : minorWidth} opacity={isMajor ? 1 : 0.4} />
      );
    }
    return (
      <Svg width={width} height={height}>
        <Background width={width} height={height} />
        {lines}
      </Svg>
    );
  }, [spacing, minorWidth, majorWidth, color, width, height]);
}

function LinedPaper({ spacing, minorWidth, color, width, height, marginLeft }) {
  return useMemo(() => {
    const lines = [];
    const sp = toPx(spacing);
    const lw = minorWidth;
    const ml = marginLeft != null ? toPx(marginLeft) : 0;

    lines.push(
      <line key="margin" x1={ml} y1={0} x2={ml} y2={height} stroke={color} strokeWidth={lw * 2} opacity={0.6} />
    );

    for (let y = sp; y <= height; y += sp) {
      lines.push(
        <line key={`h${y}`} x1={ml} y1={y} x2={width} y2={y} stroke={color} strokeWidth={lw} opacity={0.4} />
      );
    }
    return (
      <Svg width={width} height={height}>
        <Background width={width} height={height} />
        {lines}
      </Svg>
    );
  }, [spacing, minorWidth, color, width, height, marginLeft]);
}

const GRID_RENDERERS = {
  square: SquareGridPattern,
  dot: DotGrid,
  isometric: IsometricGrid,
  hexagonal: HexagonalGrid,
  polar: PolarGrid,
  engineering: EngineeringGrid,
  lined: LinedPaper,
};

export { GRID_RENDERERS };

export default function GraphPaper({
  type = 'square',
  spacing = 5,
  minorWidth = 0.3,
  majorWidth = 0.8,
  color = '#1a1a1a',
  width = 816,
  height = 1056,
  accentEvery = 0,
  marginLeft = 0,
}) {
  const Renderer = GRID_RENDERERS[type];
  if (!Renderer) return null;

  return (
    <Renderer
      spacing={spacing}
      minorWidth={minorWidth}
      majorWidth={majorWidth}
      color={color}
      width={width}
      height={height}
      accentEvery={accentEvery}
      marginLeft={marginLeft}
    />
  );
}
